import Modal, { useAsyncModal } from '@/libs/antd/components/Modal';
import { Viewer } from '@react-pdf-viewer/core';
import DocxViewer from '../DocxViewer';
import { Button } from 'antd';
import { FC } from 'react';
import { saveAs } from 'file-saver';
import { createAndDownloadZip, ZipFile } from '@/utils/zip-files';
import { getFileExtensionFromSignedUrl } from '@/utils/file-utils';

export interface FileData {
	id: number;
	name: string;
	url: string;
}

export type PreviewDocumentModalParam = {
	url?: string | string[];
	fileData?: FileData | FileData[];
	allowDownload?: boolean;
	downloadAllDocuments?: boolean;
	zipName?: string;
	button?: {
		text: string;
		onClick: () => void;
	};
	buttonPosition?: 'top' | 'bottom';
};

export const usePreviewDocumentModal = () => {
	return useAsyncModal<PreviewDocumentModalParam, void>();
};

export type PreviewDocumentModalProps = ReturnType<
	typeof usePreviewDocumentModal
>;

const PreviewDocumentModal: FC<PreviewDocumentModalProps> = ({
	modal,
	data,
	close,
}) => {
	// Handle both URL strings and file data objects
	let documentURLs: string[] = [];
	let fileDataArray: FileData[] = [];
	const buttonPosition = data?.buttonPosition ?? 'bottom';

	if (data?.fileData) {
		const fileData = Array.isArray(data.fileData)
			? data.fileData
			: [data.fileData];
		fileDataArray = fileData;
		documentURLs = fileData.map((file) => file.url);
	} else if (data?.url) {
		documentURLs = Array.isArray(data.url) ? data.url : [data.url];
	}

	/**
	 * Sanitizes filename by removing or replacing problematic characters
	 */
	const sanitizeFilename = (filename: string): string => {
		// Replace characters that can cause issues in file systems
		return filename
			.replace(/[/\\:*?"<>|]/g, '_') // Replace path separators and problematic chars with underscore
			.replace(/\s+/g, ' ') // Normalize whitespace
			.replace(/^\.+/, '') // Remove leading dots
			.replace(/\.+$/, '') // Remove trailing dots
			.replace(/\.[^.]+$/, '') // Remove file extension if any
			.trim();
	};

	/**
	 * Extracts file extension from signed URL
	 */
	const getFileExtensionFromUrl = (url: string): string => {
		const extension = getFileExtensionFromSignedUrl(url);
		return extension || '.pdf'; // Default to PDF if no extension found
	};

	// Create file objects for zip download prioritizing URL filename extraction
	const zipFiles: ZipFile[] = documentURLs.map((url, index) => {
		const fileData = fileDataArray[index];

		// First priority: Extract filename from URL
		const extension = getFileExtensionFromUrl(url);

		if (extension && extension !== '.pdf') {
			// Use filename from URL (which should include extension)
			const sanitizedFilename = sanitizeFilename(
				`${fileData?.name || 'document'}${extension}`,
			);
			return {
				name: sanitizedFilename,
				url,
			};
		} else if (fileData) {
			// Second priority: Use fileData.name with extension from URL
			const baseName = sanitizeFilename(fileData.name);
			const extension = getFileExtensionFromUrl(url);
			return {
				name: `${baseName}${extension}`,
				url,
			};
		} else {
			// Final fallback to URL path extraction (for backward compatibility)
			const questionMarkIndex = url.indexOf('?');
			const urlPath =
				questionMarkIndex !== -1 ? url.substring(0, questionMarkIndex) : url;
			const lastSlashIndex = urlPath.lastIndexOf('/');
			const filename = urlPath.substring(lastSlashIndex + 1);
			const cleanFilename = decodeURIComponent(filename.split('?')[0]);
			const extension = getFileExtensionFromUrl(url);
			const sanitizedFilename = sanitizeFilename(cleanFilename);

			return {
				name: `${sanitizedFilename}${extension}`,
				url,
			};
		}
	});

	const handleDownloadAll = async () => {
		try {
			await createAndDownloadZip(zipFiles, data?.zipName ?? 'Documents');
		} catch (error) {
			console.error('Failed to download all documents:', error);
		}
	};

	const render = (url: string) => {
		// Get extension from signed URL
		const extension = getFileExtensionFromUrl(url);

		switch (extension) {
			case '.pdf':
				return <Viewer fileUrl={url} />;
			case '.docx':
				return <DocxViewer url={url} />;
			case '.jpg':
			case '.jpeg':
			case '.png':
				return (
					<img
						style={{
							display: 'block',
							width: '100%',
							margin: '0 auto',
							height: 'auto',
						}}
						src={url}
					/>
				);
			default:
				return null;
		}
	};

	return (
		<Modal
			{...modal}
			closable
			width={1440}
			footer={null}
			className="image-modal"
			styles={{
				content: {
					padding: 10,
					border: 0,
					textAlign: 'center',
				},
			}}
			okButtonProps={{
				hidden: true,
			}}
			cancelButtonProps={{
				hidden: true,
			}}
		>
			{buttonPosition === 'top' && (
				<div
					style={{
						display: 'flex',
						justifyContent: 'center',
						gap: 10,
						marginBottom: 20,
					}}
				>
					{data?.downloadAllDocuments && documentURLs.length > 0 && (
						<Button
							type="primary"
							onClick={handleDownloadAll}
						>
							Download All Documents
						</Button>
					)}
					{data?.button && (
						<Button
							type="primary"
							onClick={() => {
								close();
								data.button?.onClick?.();
							}}
						>
							{data.button.text}
						</Button>
					)}
				</div>
			)}
			<div
				style={{
					display: 'flex',
					flexDirection: 'column',
					gap: 30,
				}}
			>
				{documentURLs.map((url, index) => (
					<div
						key={`${url}-${index}`}
						style={{ position: 'relative' }}
					>
						{render(url)}
						{data?.allowDownload && !data?.downloadAllDocuments && (
							<Button
								type="primary"
								style={{
									width: 200,
									position: 'sticky',
									left: '50%',
									bottom: 10,
									zIndex: 10,
									transform: data?.button
										? 'translateX(-100%)'
										: 'translateX(-50%)',
								}}
								onClick={() => {
									saveAs(url);
								}}
							>
								DOWNLOAD
							</Button>
						)}
					</div>
				))}
				{buttonPosition === 'bottom' && (
					<div
						style={{
							display: 'flex',
							justifyContent: 'center',
							gap: 10,
							marginTop: 20,
						}}
					>
						{data?.downloadAllDocuments && documentURLs.length > 0 && (
							<Button
								type="primary"
								onClick={handleDownloadAll}
							>
								Download All Documents
							</Button>
						)}
						{data?.button && (
							<Button
								type="primary"
								onClick={() => {
									close();
									data.button?.onClick?.();
								}}
							>
								{data.button.text}
							</Button>
						)}
					</div>
				)}
			</div>
		</Modal>
	);
};

export default PreviewDocumentModal;
